import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  /** Stroke/fill colour. Defaults to the brand teal. Pass "white" for dark backgrounds. */
  color?: string;
}

/**
 * CAF-Pilot SVG wordmark logo.
 *
 * Traced from the brand asset. Uses an explicit `color` prop so it works on
 * both light and dark backgrounds without CSS filter hacks.
 */
export function Logo({ className, color = "#1B7080" }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 412 82"
      fill="none"
      aria-label="CAF-Pilot"
      role="img"
      className={cn("h-auto", className)}
    >
      <g
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* ── C ─────────────────────────────────────────────────────────── */}
        {/* Open arc ~280°, gap facing right */}
        <path d="M 56.5,22 A 28,28 0 1,0 56.5,58" />

        {/* ── A ─────────────────────────────────────────────────────────── */}
        {/* Two diagonal legs */}
        <line x1="73" y1="69" x2="97" y2="11" />
        <line x1="97" y1="11" x2="121" y2="69" />
        {/* Broken crossbar at y=47 */}
        <line x1="83" y1="47" x2="91" y2="47" />
        <line x1="103" y1="47" x2="111" y2="47" />

        {/* ── F ─────────────────────────────────────────────────────────── */}
        {/* Vertical stroke */}
        <line x1="131" y1="11" x2="131" y2="69" />
        {/* Top bar — floats with a gap from the vertical */}
        <line x1="140" y1="11" x2="172" y2="11" />
        {/* Middle bar — shorter, same gap */}
        <line x1="140" y1="40" x2="162" y2="40" />

        {/* ── hyphen ────────────────────────────────────────────────────── */}
        <line x1="180" y1="40" x2="196" y2="40" />

        {/* ── P ─────────────────────────────────────────────────────────── */}
        {/* Vertical stroke */}
        <line x1="205" y1="11" x2="205" y2="69" />
        {/* Semicircular bump, slightly open at bottom */}
        <path d="M 205,11 A 23,23 0 0,1 205,57" />

        {/* ── i (lowercase) ─────────────────────────────────────────────── */}
        <line x1="240" y1="30" x2="240" y2="69" />
        <circle cx="240" cy="20" r="3" fill={color} stroke="none" />

        {/* ── L ─────────────────────────────────────────────────────────── */}
        <line x1="252" y1="11" x2="252" y2="69" />
        <line x1="252" y1="69" x2="287" y2="69" />

        {/* ── O ─────────────────────────────────────────────────────────── */}
        <circle cx="320" cy="40" r="27" />

        {/* ── T (right-pointing triangle arrow) ────────────────────────── */}
        {/* Right vertical edge */}
        <line x1="401" y1="11" x2="401" y2="69" />
        {/* Top diagonal to left apex */}
        <line x1="401" y1="11" x2="357" y2="40" />
        {/* Bottom diagonal from left apex */}
        <line x1="357" y1="40" x2="401" y2="69" />
      </g>
    </svg>
  );
}
